import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RouteLocation = () => {
  const sessionId = localStorage.getItem("currentSessionId"); // current session
  const [routes, setRoutes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [selectedRouteName, setSelectedRouteName] = useState('');
  const [applyMethod, setApplyMethod] = useState('');
  const [monthlyFees, setMonthlyFees] = useState({});
  const [applyAllChecked, setApplyAllChecked] = useState(false);
  const [existingCharges, setExistingCharges] = useState([]);
  const [editingCharge, setEditingCharge] = useState(null);

  const months = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];
const showMessage = (msg) => {
  alert(msg);
};

  // Fetch all routes and charges for the session
  useEffect(() => {
    if (!sessionId) return;
    fetchRoutes();
    fetchRouteCharges();
  }, [sessionId]);

  // Fetch locations when a route is selected
useEffect(() => {
  if (selectedRoute && !editingCharge) {
    fetchLocations(selectedRoute);
  }
}, [selectedRoute, applyMethod, routes, editingCharge]);


  // Fetch routes filtered by session
const fetchRoutes = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/busroutes/getBusRoutes`, {
      params: { sessionId }
    });
    console.log('Fetched Routes:', response.data);  // Log the response to check if it contains routes

    // Filter locations by session
    const sessionRoutes = response.data.map(route => ({
      ...route,
      locations: route.locations.filter(
        loc => loc.session === sessionId || loc.session?._id === sessionId
      )
    }));

    console.log('Filtered Session Routes:', sessionRoutes);  // Check filtered routes

    setRoutes(sessionRoutes);  // Update the state with the routes
  } catch (error) {
    console.error('Error fetching routes:', error);
    showMessage('error', 'Failed to fetch bus routes.');
  }
};

  // Fetch locations of the selected route
  const fetchLocations = (routeId) => {
    const selected = routes.find(r => r._id === routeId);
    if (!selected) return;

    setSelectedRouteName(selected.routeName);

    const sessionLocations = selected.locations.filter(
      loc => loc.session === sessionId || loc.session?._id === sessionId
    );

    setLocations(sessionLocations);

    // Only initialize fees if not editing an existing charge
    if (!editingCharge) {
  if (applyMethod === 'viaLocation') {
    initializeFeesForLocations(sessionLocations);
  }
  if (applyMethod === 'viaRoute') {
    setMonthlyFees({ default: Array(12).fill('0') });
  }
}

  };

  // Fetch existing route charges for session
  const fetchRouteCharges = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/routeCharges/getRouteCharges`, {
        params: { sessionId }
      });
      setExistingCharges(response.data || []);
    } catch (error) {
      console.error('Error fetching route charges:', error);
    }
  };

  // Initialize monthly fees for each location
  const initializeFeesForLocations = (locations) => {
    const initialFees = {};
    locations.forEach(loc => initialFees[loc.location] = Array(12).fill('0'));
    setMonthlyFees(initialFees);
  };

  const handleMonthlyFeesChange = (location, index, value) => {
    setMonthlyFees(prev => {
      const updated = { ...prev };
      if (!updated[location]) updated[location] = Array(12).fill('0');
      updated[location][index] = value;
      return updated;
    });
  };

  const handleApplyAllChange = () => {
    setApplyAllChecked(!applyAllChecked);
    if (!applyAllChecked) {
      const updated = { ...monthlyFees };
      Object.keys(updated).forEach(location => {
        const firstFee = updated[location][0] || '0';
        updated[location] = Array(12).fill(firstFee);
      });
      setMonthlyFees(updated);
    }
  };

  // Submit or update route charges
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) return alert('Session ID is required.');

    let charges = [];

    if (applyMethod === 'viaRoute') {
      charges = locations.map(loc => ({
        location: loc.location,
        monthlyFees: (monthlyFees['default'] || []).map((amount, i) => ({
          month: months[i],
          amount: parseFloat(amount) || 0
        }))
      }));
    } else if (applyMethod === 'viaLocation') {
      charges = Object.entries(monthlyFees).map(([loc, fees]) => ({
        location: loc,
        monthlyFees: fees.map((amount, i) => ({
          month: months[i],
          amount: parseFloat(amount) || 0
        }))
      }));
    }

    const data = { routeName: selectedRouteName, charges, method: applyMethod, sessionId };

    try {
      if (editingCharge) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/routeCharges/updateRouteCharge/${selectedRouteName}/${editingCharge.location}`,
          data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }
        );
        alert('Charges updated successfully');
        setEditingCharge(null);
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/routeCharges/createRouteCharge`, data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }
        );
        alert('Charges added successfully');
      }
      fetchRouteCharges();
      setSelectedRoute('');
      setSelectedRouteName('');
      setApplyMethod('');
      setMonthlyFees({});
      setApplyAllChecked(false);
    } catch (error) {
      console.error('Error saving charges:', error);
      alert('Failed to save route charges.');
    }
  };

  const handleDeleteCharge = async (routeName, location) => {
    if (!sessionId) return alert('Session ID is required.');
    if (!window.confirm('Are you sure you want to delete this charge?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/routeCharges/deleteRouteCharge/${routeName}/${location}?sessionId=${sessionId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      fetchRouteCharges();
    } catch (error) {
      console.error('Error deleting charge:', error);
    }
  };

  const handleUpdateCharge = (charge) => {
  setEditingCharge(charge);
  setSelectedRouteName(charge.routeName);
  setApplyMethod(charge.method);

  // set selected route ID
  const route = routes.find(r => r.routeName === charge.routeName);
  if (route) setSelectedRoute(route._id);

  if (charge.method === "viaRoute") {
    // ✅ only one location exists in viaRoute
    const routeFees = charge.charges[0].monthlyFees.map(f =>
      f.amount.toString()
    );

    setMonthlyFees({
      default: routeFees
    });

    setLocations(charge.charges.map(c => ({ location: c.location })));
  }

  if (charge.method === "viaLocation") {
    const fees = {};
    charge.charges.forEach(loc => {
      fees[loc.location] = loc.monthlyFees.map(f =>
        f.amount.toString()
      );
    });

    setMonthlyFees(fees);
    setLocations(charge.charges.map(c => ({ location: c.location })));
  }
};


  const calculateTotalCharges = (fees) => fees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);

  // Handle route change
  const handleRouteChange = (e) => {
    setSelectedRoute(e.target.value);
  };

  return (
    <div className="container mx-auto p-4">
      <form onSubmit={handleSubmit} className="mb-4">
        {/* Select Route */}
        <div className="mb-2">
          <label className="block text-gray-700">Select Route:</label>
          <select
            value={selectedRoute}
            onChange={handleRouteChange}
            className="w-full p-2 border border-gray-300 rounded"
            required
          >
            <option value="" disabled>Select Route</option>
            {routes.map(route => (
              <option key={route._id} value={route._id}>{route.routeName}</option>
            ))}
          </select>
        </div>

        {/* Apply Method */}
        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Apply Transport Fee:</label>
          <div className="flex items-center">
            <input type="radio" id="viaRoute" name="applyMethod" value="viaRoute"
              checked={applyMethod === 'viaRoute'}
              onChange={() => { setApplyMethod('viaRoute'); setMonthlyFees({ default: Array(12).fill('0') }); }}
              className="mr-2" />
            <label htmlFor="viaRoute" className="mr-4">Via Route</label>
            <input type="radio" id="viaLocation" name="applyMethod" value="viaLocation"
              checked={applyMethod === 'viaLocation'}
              onChange={() => { setApplyMethod('viaLocation'); initializeFeesForLocations(locations); }}
              className="mr-2" />
            <label htmlFor="viaLocation">Via Location</label>
          </div>
        </div>

        {/* Monthly Fees */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Fees:</label>
          {applyMethod === 'viaRoute' && months.map((month, idx) => (
            <div key={idx} className="flex items-center mb-2">
              <label className="w-24">{month}</label>
              <input type="number" min="0" required
                value={monthlyFees['default'] ? monthlyFees['default'][idx] : '0'}
                onChange={e => handleMonthlyFeesChange('default', idx, e.target.value)}
                className="p-2 border border-gray-300 rounded mr-2"
              />
              {idx === 0 && (
                <label className="flex items-center">
                  <input type="checkbox" checked={applyAllChecked} onChange={handleApplyAllChange} className="mr-1" />
                  Apply to all
                </label>
              )}
            </div>
          ))}

          {applyMethod === 'viaLocation' && Object.keys(monthlyFees).map(loc => (
            <div key={loc} className="mb-4">
              <h4 className="text-lg font-semibold mb-2">{loc}</h4>
              <div className="grid grid-cols-12 gap-2">
                {months.map((month, idx) => (
                  <div key={idx} className="col-span-1">
                    <label className="block text-sm">{month}</label>
                    <input type="number" min="0" required
                      value={monthlyFees[loc][idx]}
                      onChange={e => handleMonthlyFeesChange(loc, idx, e.target.value)}
                      className="p-1 border border-gray-300 rounded"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded mt-2">
          {editingCharge ? 'Update Charges' : 'Add Charges'}
        </button>
      </form>

      {/* Existing Charges Table */}
      <h2 className="text-xl font-bold mb-2">Existing Route Charges</h2>
      <table className="min-w-full bg-white border">
        <thead className="bg-gray-200">
          <tr>
            <th className="px-4 py-2 border">Route</th>
            <th className="px-4 py-2 border">Location</th>
            <th className="px-4 py-2 border">Total Charges</th>
            <th className="px-4 py-2 border">Actions</th>
          </tr>
        </thead>
        <tbody className="text-center">
          {existingCharges.map(route => route.charges.map((locCharge, idx) => (
            <tr key={`${route.routeName}-${locCharge.location}`}>
              {idx === 0 && <td rowSpan={route.charges.length} className="px-4 py-2 border">{route.routeName}</td>}
              <td className="px-4 py-2 border">{locCharge.location}</td>
              <td className="px-4 py-2 border">{calculateTotalCharges(locCharge.monthlyFees)}</td>
              <td className="px-4 py-2 border">
                <button className="bg-yellow-500 text-white px-2 py-1 rounded mr-1"
                  onClick={() => handleUpdateCharge(route)}>Edit</button>
                <button className="bg-red-500 text-white px-2 py-1 rounded"
                  onClick={() => handleDeleteCharge(route.routeName, locCharge.location)}>Delete</button>
              </td>
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
};

export default RouteLocation;
